import React, { useRef, useEffect } from 'react'
import { Grid, Button, Container, Box, Typography } from '@mui/material'
import { useLocation } from 'react-router'
import Script from 'react-load-script'
import {
  marketingSectionSx,
  marketingHeadingSx,
  marketingSubheadingSx,
  marketingFieldGroupSx,
  marketingFieldSx,
  marketingTextareaSx,
  marketingFieldHintSx,
} from '@/housefolios-components/PublicPages/publicPageSx'

/**
 * Contact section component for public home page
 * Handles both contact forms and Calendly integration
 */
const ContactSection = ({
  marketplace,
  orgName,
  urlAffiliate,
  affiliate,
  name,
  setName,
  email,
  setEmail,
  phone,
  setPhone,
  message,
  setMessage,
  sendEmail,
}) => {
  const location = useLocation()
  const contactUs = useRef(null)

  useEffect(() => {
    if (location.hash && contactUs.current) {
      contactUs.current.scrollIntoView({ behavior: 'smooth'  })
    }
  }, [location])

  const isURLAbsolute = (url) => {
    return url.indexOf('://') > 0 || url.indexOf('//') === 0
  }

  // Special case for nlinvesting marketplace
  if (marketplace?.url === 'nlinvesting.housefolios.com') {
    return (
      <Box component="section" sx={marketingSectionSx} id="contact">
        <Grid container>
          <Grid
            size={12}
            container
            direction="row"
            sx={{
              justifyContent: 'center',
            }}
          >
            <Grid size={12} sx={{ textAlign: 'center' }}>
              <Typography variant="h2" component="h2" sx={marketingHeadingSx}>
                ALREADY HAVE A PROPERTY?
              </Typography>
              <Typography
                variant="h3"
                component="h3"
                sx={[marketingSubheadingSx, { fontWeight: 700, color: '#a6a6b9' }]}
              >
                Click the button below to have us check it out
              </Typography>
            </Grid>
          </Grid>
          <Grid
            size={12}
            container
            direction="row"
            sx={{
              justifyContent: 'center',
            }}
          >
            <Grid size={12} style={{ textAlign: 'center' }}>
              <div id="success"></div>
              <Button
                variant="contained"
                color="primary"
                component="a"
                href="https://housefolios.com/nextlevel"
                style={{
                  color: 'white',
                  fontWeight: 700,
                  padding: '0.5rem 1rem',
                  fontSize: '1.25rem',
                  lineHeight: '1.5',
                  marginLeft: 5,
                }}
              >
                CHECK IT OUT
              </Button>
            </Grid>
          </Grid>
        </Grid>
    </Box>
    )
  }

  return (
    <Box component="section" sx={marketingSectionSx} id="contact">
      <Container maxWidth="md">
        <Grid container>
          <Grid size={12} sx={{ textAlign: 'center' }}>
            <Typography variant="h2" component="h2" sx={marketingHeadingSx}>
              Contact Us Today
            </Typography>
            <Typography
              variant="h3"
              component="h3"
              sx={[marketingSubheadingSx, { fontWeight: 700, color: '#a6a6b9' }]}
            >
              Any questions? Contact someone at{' '}
              {marketplace && marketplace.meetingURL
                ? orgName
                : urlAffiliate
                  ? affiliate?.companyName || 'Housefolios'
                  : 'Housefolios'}{' '}
              today!
            </Typography>
          </Grid>
          <Grid size={12} ref={contactUs}>
            {marketplace &&
            marketplace.meetingURL &&
            isURLAbsolute(marketplace.meetingURL) ? (
              <div id="calendly" style={{ height: '650px' }} />
            ) : (
              <>
                <iframe
                  name="dummyframe"
                  id="dummyframe"
                  style={{ display: 'none' }}
                />
                <form
                  action="sendEmail.php"
                  target="dummyframe"
                  name="contactForm"
                  noValidate
                  onSubmit={sendEmail}
                >
                  <Grid container direction="row" spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box sx={marketingFieldGroupSx}>
                        <Box component="input"
                          value={name}
                          onChange={(event) =>
                          setName(event.target.value)
                          }
                          id="name"
                          name="name"
                          type="text"
                          placeholder="Your Name *"
                          required
                          style={{ fontWeight: 700, width: '100%' }}
                          data-validation-required-message="Please enter your name." sx={[marketingFieldSx, { fontWeight: 700 }]}
                          />
                        <Box
                          component="p"
                          sx={[marketingFieldHintSx, { color: '#f83245' }]}
                        ></Box>
                      </Box>
                      <Box sx={marketingFieldGroupSx}>
                        <Box component="input"
                          value={email}
                          onChange={(event) =>
                          setEmail(event.target.value)
                          }
                          id="email"
                          name="email"
                          type="email"
                          placeholder="Your Email *"
                          required
                          style={{ fontWeight: 700, width: '100%' }}
                          data-validation-required-message="Please enter your email address." sx={[marketingFieldSx, { fontWeight: 700 }]}
                          />
                        <Box
                          component="p"
                          sx={[marketingFieldHintSx, { color: '#f83245' }]}
                        ></Box>
                      </Box>
                      <Box sx={marketingFieldGroupSx}>
                        <Box component="input"
                          value={phone}
                          onChange={(event) =>
                          setPhone(event.target.value)
                          }
                          id="phone"
                          name="phone"
                          type="tel"
                          placeholder="Your Phone *"
                          required
                          style={{ fontWeight: 700, width: '100%' }}
                          data-validation-required-message="Please enter your phone number." sx={[marketingFieldSx, { fontWeight: 700 }]}
                          />
                        <Box
                          component="p"
                          sx={[marketingFieldHintSx, { color: '#f83245' }]}
                        ></Box>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box sx={marketingFieldGroupSx}>
                        <Box component="textarea"
                          value={message}
                          onChange={(event) =>
                          setMessage(event.target.value)
                          }
                          id="message"
                          name="message"
                          placeholder="Your Message *"
                          required
                          style={{ fontWeight: 700, width: '100%' }}
                          data-validation-required-message="Please enter a message." sx={[marketingTextareaSx, { fontWeight: 700 }]}
                          />
                        <Box
                          component="p"
                          sx={[marketingFieldHintSx, { color: '#f83245' }]}
                        ></Box>
                      </Box>
                    </Grid>
                    <Grid size={12} style={{ textAlign: 'center' }}>
                      <div id="success"></div>
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        id="sendMessageButton"
                        name="action"
                        value="Send Message"
                        style={{
                        color: 'white',
                        fontWeight: 700,
                        padding: '0.5rem 1rem',
                        }}
                        sx={{ textTransform: 'uppercase' }}
                        >
                        Send Message
                      </Button>
                    </Grid>
                  </Grid>
                </form>
                          </>
            )}
          </Grid>
        </Grid>
      </Container>

      {marketplace &&
        marketplace.meetingURL &&
        isURLAbsolute(marketplace.meetingURL) && (
          <Script
            url={`https://assets.calendly.com/assets/external/widget.js`}
            onLoad={() => {
              window.Calendly.initInlineWidget({
                url:
                  marketplace && marketplace.meetingURL
                    ? marketplace.meetingURL
                    : 'https://calendly.com/jaredgarfield',
                parentElement: document.getElementById('calendly'),
                prefill: {},
                utm: {},
              })
            }}
          />
        )}
    </Box>
  )
}

export default ContactSection
